import React, { useState } from "react";
import { Badge } from "../../../../_helpers/components/Badge";
import { formatTime } from "../../../../_helpers/functions";
import { useOutletContext } from "react-router-dom";
import { attachShift, detachShift } from "../../../../_api/EventShift";
import { Button } from "../../../../_helpers/components/Button";
import toast from "react-hot-toast";

export const EventAdminView = ({ shifts, reloadEvent }) => {
    const [user, setUser] = useOutletContext();
    // loadingShift : id du shift en cours de requête (ou null si aucun)
    const [loadingShift, setLoadingShift] = useState(null);
    const [viewCommentShift, setViewCommentShift] = useState(null); // shift dont on veut voir le commentaire

    // Regrouper les créneaux par date
    const groupedShifts = shifts.reduce((acc, creneau) => {
        const dateKey = new Date(creneau.start_time).toLocaleDateString(
            "fr-FR",
            {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
            }
        );

        if (!acc[dateKey]) {
            acc[dateKey] = [];
        }
        acc[dateKey].push(creneau);
        return acc;
    }, {});

    // Spinner simple Tailwind
    const Spinner = () => (
        <svg
            className="animate-spin h-5 w-5 text-white mx-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            ></circle>
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
        </svg>
    );

    return (
        <div className="w-full flex flex-col gap-6 mt-6 md:mt-0 pl-0 md:pl-6">
            {Object.entries(groupedShifts).map(([date, creneaux]) => (
                <div key={date}>
                    <h2 className="text-lg font-bold text-gray-700 mb-3">
                        {date}
                    </h2>

                    <div className="flex flex-wrap gap-4">
                        {creneaux.map((creneau) => {
                            const remainingSlots =
                                creneau.users_limit - creneau.users.length;
                            const hasManyParticipants =
                                creneau.users.length > 10;
                            const registered_shifts_ids = user.shifts.map(
                                (shift) => shift.id
                            );
                            const isRegistered = registered_shifts_ids.includes(
                                creneau.id
                            );
                            const isLoading = loadingShift === creneau.id;

                            const isReadOnly = !(!creneau.skill_id || user.skills.some(s => s.id === creneau.skill_id));

                            const handleAttach = () => {
                                setLoadingShift(creneau.id);
                                const payload = {
                                    userID: user.id,
                                    shiftID: creneau.id,
                                };
                                attachShift(payload)
                                    .then((resp) => {
                                        toast(resp.data.message);
                                        setUser(resp.data.data.user);
                                        reloadEvent();
                                    })
                                    .catch((err) => {
                                        console.log(err);
                                        toast.error(
                                            "Erreur lors de l'inscription."
                                        );
                                    })
                                    .finally(() => setLoadingShift(null));
                            };

                            const handleDetach = () => {
                                setLoadingShift(creneau.id);
                                const payload = {
                                    userID: user.id,
                                    shiftID: creneau.id,
                                };
                                detachShift(payload)
                                    .then((resp) => {
                                        toast(resp.data.message);
                                        setUser(resp.data.data.user);
                                        reloadEvent();
                                    })
                                    .catch(() => {
                                        toast.error(
                                            "Vous ne pouvez pas annuler votre inscription."
                                        );
                                    })
                                    .finally(() => setLoadingShift(null));
                            };
                            
                            return (
                                <div
                                    key={creneau.id}
                                    className="relative p-4 border rounded-lg bg-gray-50 text-center flex-initial min-w-min w-full md:w-56 pt-6"
                                >
                                    {creneau.comment && (
                                        <div className="absolute top-2 left-2">
                                            <button
                                                onClick={() => setViewCommentShift(creneau)}
                                                title="Voir les consignes"
                                                className="relative text-yellow-500 hover:text-yellow-700 flex gap-2"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-5 h-5 cursor-pointer"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth={2}
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 9v2m0 4h.01M12 2a10 10 0 110 20 10 10 0 010-20z"
                                                    />
                                                </svg>
                                                <span className="-ml-1 text-xs">+ d'info</span>
                                            </button>
                                        </div>
                                    )}

                                    <div className="text-center m-2">
                                        <Badge
                                            text={
                                                creneau?.skill?.description ||
                                                "Ouvert à tous"
                                            }
                                            color={
                                                creneau?.skill?.description
                                                    ? "blue"
                                                    : "gray"
                                            }
                                        />
                                    </div>
                                    <p className="font-semibold text-center mb-2">
                                        {formatTime(
                                            new Date(creneau.start_time)
                                        )}{" "}
                                        -{" "}
                                        {formatTime(new Date(creneau.end_time))}
                                    </p>
                                    <hr className="m-2" />
                                    {!isReadOnly && !isRegistered &&
                                        !["CANCELLED", "CLOSED"].includes(
                                            creneau.status
                                        ) && (
                                            <Button
                                                type={"empty"}
                                                name={
                                                    isLoading ? (
                                                        <Spinner />
                                                    ) : (
                                                        "S'inscrire"
                                                    )
                                                }
                                                action={() => {
                                                    if (isLoading) return;
                                                    handleAttach();
                                                }}
                                                disabled={isLoading}
                                            />
                                        )}
                                    {isRegistered &&
                                        !["CANCELLED", "CLOSED"].includes(
                                            creneau.status
                                        ) && (
                                            <Button
                                                type={"red"}
                                                name={
                                                    isLoading ? (
                                                        <Spinner />
                                                    ) : (
                                                        "Se\u00a0désinscrire"
                                                    )
                                                }
                                                isReadOnly={[
                                                    "CLOSED",
                                                    "CANCELLED",
                                                ].includes(creneau.status)
                                                }
                                                action={() => {
                                                    if (isLoading) return;
                                                    handleDetach();
                                                }}
                                                disabled={isLoading}
                                            />
                                        )}

                                    {remainingSlots > 0 && (
                                        <p className="text-center text-sm text-red-500 mt-2">
                                            Il reste {remainingSlots} créneau(s)
                                            à pourvoir.
                                        </p>
                                    )}
                                    <hr className="m-2" />

                                    <ul
                                        className={`space-y-2 ${hasManyParticipants
                                            ? "max-h-60 overflow-auto"
                                            : ""
                                            }`}
                                    >
                                        {creneau.users.map(
                                            (participation, index) => {
                                                let statusColor = "green";

                                                if (
                                                    ["PENDING", "WAITING_LIST"].includes(participation.pivot.status)
                                                ) {
                                                    statusColor = "yellow";
                                                } else if (
                                                    [
                                                        "CLOSED",
                                                        "CANCELLED",
                                                        "WITHDRAWAL",
                                                        "REFUSED",
                                                    ].includes(
                                                        participation.pivot
                                                            .status
                                                    )
                                                ) {
                                                    statusColor = "red";
                                                }

                                                return (
                                                    <li
                                                        key={index}
                                                        className="py-1 border-b flex justify-between items-center px-2"
                                                    >
                                                        <Badge
                                                            color={statusColor}
                                                            text={participation.last_name + " " + participation.first_name}
                                                        />
                                                    </li>
                                                );
                                            }
                                        )}
                                    </ul>
                                    {viewCommentShift && (
                                        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                                            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                                                <h3 className="text-lg font-semibold mb-4">Instruction pour cet évènement : </h3>
                                                <p className="text-gray-700">{viewCommentShift.comment}</p>
                                                <div className="flex justify-end mt-4">
                                                    <button
                                                        onClick={() => setViewCommentShift(null)}
                                                        className="px-4 py-2 rounded-sm bg-gray-200 hover:bg-gray-300"
                                                    >
                                                        Fermer
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};
